/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { useTheme, Container, Text, Flex, VStack, Box } from '@chakra-ui/react';

import Image from '@rsc/ui-components/tailwind/Image';
import CTAButton from '@rsc/ui-components/tailwind/CTAButton';
import { generateSectionId } from '@rsc/ui-components/dept/utils/generateSectionId';
import Link from '@rsc/ui-components/tailwind/Link';

import { useUiComponentsInternationalContext } from '@rsc/static-translations';

export default function FeaturedBlogPost({ block }) {
  const theme = useTheme();
  const { headline, byline, cta, post } = block;
  const { tt } = useUiComponentsInternationalContext();

  return (
    <Container
      {...generateSectionId(block)}
      py={{
        base: 14,
        lg: 20,
      }}
      pos="relative"
    >
      <Box
        pos="absolute"
        top={0}
        left={0}
        w="100%"
        h="100%"
        bgImg={theme.images.circlesPattern}
        filter="grayscale(1)"
        opacity={0.05}
        bgSize="cover"
        bgPosition="center"
        zIndex={0}
      />
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', lg: 'end' }}
        flexDir={{ base: 'column', lg: 'row' }}
        gap={{ base: 4, md: 8 }}
        mb={10}
      >
        <Flex flexDir="column">
          <Text as="h2" textStyle="h1" fontWeight="black">
            {headline}
          </Text>
          <Text
            as="h3"
            textStyle={{ base: 'h4_5_6', lg: 'h3' }}
            fontWeight={{ base: 'bold', lg: 'black' }}
            color="typography.light"
          >
            {byline}
          </Text>
        </Flex>
        <CTAButton
          className="hidden z-10 lg:flex"
          variant="primary"
          cta={cta}
        />
      </Flex>
      {post && (
        <Flex
          pos="relative"
          bg="white"
          borderRadius="3xl"
          overflow="hidden"
          boxShadow="0px 4px 56px 0px rgba(5, 37, 77, 0.15)"
          zIndex={1}
          flexDir={{ base: 'column', lg: 'row' }}
        >
          {post?.image && (
            <Image
              className="w-full [&>img]:w-full min-w-full h-[20.4rem] lg:w-[28.6rem] lg:min-w-[28.6rem] lg:h-[28.6rem]"
              ratio={null}
              sanityImage={post.image}
              srcSetBasisWidth={null}
            />
          )}
          <VStack
            align="start"
            p={{
              base: 4,
              lg: 10,
            }}
          >
            <Text
              textStyle={{ lg: 'bodyLarge' }}
              fontWeight="bold"
              color="typography.light"
            >
              {post.description ?? post.categories?.[0]?.title}
            </Text>
            <Text
              as="h2"
              fontWeight={{ base: 'bold', lg: 'black' }}
              textStyle={{ base: 'h3', lg: 'h2' }}
              color="brand.ink"
            >
              {post.title}
            </Text>
            <Flex flexDirection="column">
              <Text
                textStyle={{ base: 'caption ', lg: 'bodyLarge' }}
                color="typography.light"
                noOfLines={{ base: 3, lg: 4 }}
              >
                {post.description}
              </Text>
              <Text
                as={Link}
                className="text-brand-foreground underline"
                display={{ base: 'none ', lg: 'block' }}
                link={`/blog/${post.slug?.current}`}
                textStyle={{ base: 'caption ', lg: 'bodyLarge' }}
              >
                {tt(s => s.blocksDeptComponents.readMoreLabel)}
              </Text>
            </Flex>
          </VStack>
        </Flex>
      )}
      <CTAButton
        className="flex lg:hidden w-full mt-6 relative"
        variant="primary"
        cta={cta}
      />
    </Container>
  );
}

FeaturedBlogPost.propTypes = {
  block: PropTypes.shape({}).isRequired,
};
